import { useQuery } from 'react-query';

import type { APIError } from 'utils/errors';
import { QueryHookOptions } from 'types/ReactQuery';

import { getRequest } from './apiRequests';

const apiUrl = '/api/csrf';

type CSRFTokenResponse = {
  csrf: string;
};

const getCSRFToken = () => getRequest<null, CSRFTokenResponse>(apiUrl);

/**
 * CSRF value from the host shell (window.ciq). It matches the Django session token (CSRF_USE_SESSIONS) and
 * does not rotate on a short client timer. Use GET /api/csrf only when this is empty or when recovering from
 * 403 `csrf_failure` (see fetchMutateWithCsrfRetry + setQueryData).
 */
const getPageCsrfBootstrap = (): string =>
  typeof window !== 'undefined' ? ((window as { ciq?: { csrfToken?: string } }).ciq?.csrfToken ?? '') : '';

const useGetCSRFToken = (options: QueryHookOptions<CSRFTokenResponse, APIError> = {}) => {
  const bootstrap = getPageCsrfBootstrap();
  const hasBootstrap = Boolean(bootstrap);

  return {
    query: useQuery<CSRFTokenResponse, APIError>({
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      retry: false,
      initialData: hasBootstrap ? { csrf: bootstrap } : undefined,
      enabled: !hasBootstrap,
      ...options,
      queryKey: ['csrf'],
      queryFn: getCSRFToken,
    }),
  };
};

export const CSRFService = {
  apiUrl,
  getPageCsrfBootstrap,
  useGetCSRFToken,
};
